<template>
  <b-sidebar
    id="Sidebar"
    width="260px"
    visible
    no-header-close
    no-close-on-esc
    no-close-on-route-change
  >
    <template #header>
      <router-link to="/">
        <img
          src="../../assets/images/logo.png"
          alt="Presence Verte"
          height="65px"
          class="mb-2"
        />
      </router-link>
    </template>
    <b-nav class="flex-column">
      <template v-for="item in navItems">
        <b-nav-item
          class="p-2 SidebarItem"
          :class="{
            active: $route.path === item.url,
            isToggle: checkIsToggle(item),
          }"
          :data-cy="`SidebarItem-${item.name}`"
          :key="item.name"
          @click.prevent="onItemClicked(item)"
        >
          <i :class="`${item.icon}`" class="mr-2"></i>
          <span class="SidebarItem-text">{{ item.name }}</span>
          <b-badge
            v-if="item.badge && badges[item.badge].show"
            pill
            variant="danger"
            :data-cy="`Sidebar-${item.url}-badge`"
            >{{ badges[item.badge].text }}</b-badge
          >
          <span v-show="item.children && item.isToggle">
            <i class="fas fa-chevron-down mt-2"></i>
          </span>
          <span v-show="item.children && !item.isToggle">
            <i class="fas fa-chevron-right mt-2"></i>
          </span>
        </b-nav-item>
        <b-nav-item
          class="sub-item"
          v-for="(child, keySubItem) in item.children"
          :data-cy="`Menu-${item.name}-subItem-${keySubItem}`"
          :key="child.name"
          :to="child.url"
          :class="{
            isShow: item.isToggle,
            isActive: $route.path === child.url,
          }"
        >
          <i :class="`${child.icon}`" class="mr-2"></i>
          <span v-if="child.name.length <= MAX_ITEM_LENGTH">{{
            child.name
          }}</span>
          <span v-else :title="child.name">{{ truncateName(child.name) }}</span>
        </b-nav-item>
      </template>
    </b-nav>
  </b-sidebar>
</template>

<script>
import { mapGetters } from 'vuex';
import { isActionAllowed } from '@/helpers/rights.js';
import { getNavItemsFromRoute } from './sideBar.ts';

const maxItemLength = 15;

export default {
  name: 'SideBar',
  data() {
    return {
      navItems: [],
    };
  },

  watch: {
    $route(to, from) {
      this.navItems = getNavItemsFromRoute(
        to.path,
        this.isPlatformAdminMenuShowed,
        this.currentUser.content.profileIds,
        this.selectedTenant
      );
    },
  },

  computed: {
    ...mapGetters('auth', [
      'currentUsername',
      'currentUserRights',
      'currentUser',
    ]),
    ...mapGetters('alerts', ['pendingAlerts', 'hasPending']),
    ...mapGetters('tenants', ['selectedTenant']),
    badges() {
      return {
        pendingAlerts: {
          show: this.hasPending,
          text: this.pendingAlerts,
        },
      };
    },
    isPlatformAdminMenuShowed() {
      return isActionAllowed(
        this.currentUserRights,
        'document',
        'search',
        'multi-tenancy',
        'tenants'
      );
    },
  },

  mounted() {
    this.navItems = getNavItemsFromRoute(
      this.$router.currentRoute.path,
      this.isPlatformAdminMenuShowed,
      this.currentUser.content.profileIds,
      this.selectedTenant
    );
  },

  methods: {
    truncateName(name) {
      return `${name.substring(0, this.MAX_ITEM_LENGTH)}...`;
    },
    onItemClicked(item) {
      if (item.children) {
        item.isToggle = !item.isToggle;
        return;
      }
      this.$router.push(item.url);
    },
    checkIsToggle(item) {
      if (item.isToggle) {
        item.isToggle = true;
        return true;
      }
      if (
        item.children &&
        item.children.filter((child) => this.$route.path === child.url).length >
          0
      ) {
        item.isToggle = true;
        return true;
      }
      item.isToggle = false;
      return item.isToggle;
    },
  },

  created() {
    this.MAX_ITEM_LENGTH = maxItemLength;
  },
};
</script>

<style lang="scss">
@mixin active-state {
  background-color: $gray-100;
  border-left: 5px solid $danger;
  border-right: 1px solid $gray-300;
}

#Sidebar .b-sidebar-header {
  border-bottom: 1px solid $gray-500;
  background-color: white;
}

#Sidebar .nav-item {
  border-bottom: 1px solid $gray-500;
  border-left: 5px solid $gray-300;

  &:hover {
    @include active-state;
    border-left: 5px solid $danger;
  }

  .fa-chevron-right,
  .fa-chevron-down {
    font-size: 12px;
    top: -2px;
    left: 0.5em;
    position: relative;
  }

  a {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .SidebarItem-text {
    flex-grow: 1;
  }
}

#Sidebar .sub-item {
  display: none;
  padding: 0.2em 0 0.2em 1.5em;
  text-align: left;

  &.isShow {
    display: block;
  }

  &.isActive {
    @include active-state;

    svg {
      color: $danger;
    }
  }
}

#Sidebar {
  .active {
    @include active-state;

    svg {
      color: $secondary;
    }
  }
}
</style>
